import React, { useEffect, useState } from "react";
import { TrendingUp, Ban } from "lucide-react";
import { api, exportUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { rupiah, num, fmtDateTime } from "@/lib/format";
import { Card, Loading, EmptyState, PageHeader, StatCard, Badge } from "@/components/common";
import DateRangeFilter, { rangeForPreset } from "@/components/DateRangeFilter";

export default function SalesReport() {
  const { branch } = useAuth();
  const [tab, setTab] = useState("products");
  const [sales, setSales] = useState(null);
  const [voids, setVoids] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dr, setDr] = useState(() => rangeForPreset("1m"));

  useEffect(() => {
    setLoading(true);
    const p = { date_from: dr.from, date_to: dr.to, ...(branch ? { branch_id: branch.id } : {}) };
    Promise.all([
      api.get("/reports/sales-products", p).catch(() => null),
      api.get("/reports/voids", p).catch(() => null),
    ]).then(([s, v]) => { setSales(s); setVoids(v); }).finally(() => setLoading(false));
  }, [branch, dr]);

  const expParams = { date_from: dr.from, date_to: dr.to, ...(branch ? { branch_id: branch.id } : {}) };

  return (
    <>
      <PageHeader title="Laporan Penjualan & Void" subtitle={branch?.name} icon={TrendingUp} />
      <div className="mb-4"><DateRangeFilter value={dr} onChange={setDr} testid="sales-range-filter" /></div>
      <div className="flex gap-2 mb-4">
        {[["products", "Produk Terlaris"], ["voids", "Order Dibatalkan"]].map(([v, l]) => (
          <button key={v} onClick={() => setTab(v)} className={`px-4 h-9 rounded-xl text-sm font-medium ${tab === v ? "bg-primary text-white" : "bg-slate-100 text-slate-600"}`}>{l}</button>
        ))}
      </div>

      {loading ? <Loading /> : tab === "products" ? (
        <>
          {sales?.totals && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <StatCard label="Total Penjualan" value={rupiah(sales.totals.gross)} tone="primary" />
              <StatCard label="Qty Terjual" value={num(sales.totals.qty)} />
              <StatCard label="Diskon" value={rupiah(sales.totals.discount)} tone="warning" />
              <StatCard label="Ongkir" value={rupiah(sales.totals.delivery_fees)} />
            </div>
          )}
          <div className="flex justify-end mb-3">
            <a href={exportUrl("sales", expParams)} target="_blank" rel="noreferrer" className="text-sm text-primary hover:underline">Ekspor Excel</a>
          </div>
          {!sales?.rows?.length ? <EmptyState icon={TrendingUp} title="Belum ada penjualan" /> : (
            <Card className="overflow-hidden">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-slate-400 border-b border-slate-100 bg-slate-50/50">
                  <th className="py-3 px-4 font-medium">Produk</th><th className="py-3 px-4 font-medium">Kategori</th>
                  <th className="py-3 px-4 font-medium text-right">Qty</th><th className="py-3 px-4 font-medium text-right">Omset</th><th className="py-3 px-4 font-medium text-right">Kontribusi</th>
                </tr></thead>
                <tbody>
                  {sales.rows.map((r, i) => (
                    <tr key={i} className="border-b border-slate-50">
                      <td className="py-3 px-4 font-medium text-slate-800">{r.name}</td>
                      <td className="py-3 px-4 text-slate-500">{r.category}</td>
                      <td className="py-3 px-4 text-right">{num(r.qty)}</td>
                      <td className="py-3 px-4 text-right font-semibold">{rupiah(r.amount)}</td>
                      <td className="py-3 px-4 text-right text-slate-400">{r.share_pct}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
        </>
      ) : (
        !voids?.rows?.length ? <EmptyState icon={Ban} title="Tidak ada order dibatalkan" /> : (
          <Card className="overflow-hidden">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-slate-400 border-b border-slate-100 bg-slate-50/50">
                <th className="py-3 px-4 font-medium">No Order</th><th className="py-3 px-4 font-medium">Dibatalkan Oleh</th><th className="py-3 px-4 font-medium">Alasan</th><th className="py-3 px-4 font-medium text-right">Nilai</th>
              </tr></thead>
              <tbody>
                {voids.rows.map((r, i) => (
                  <tr key={i} className="border-b border-slate-50">
                    <td className="py-3 px-4 font-medium text-slate-800">{r.order_no}</td>
                    <td className="py-3 px-4">{r.cancelled_by_name} <Badge className="bg-slate-100 text-slate-500 ml-1">{r.cancelled_by_role}</Badge></td>
                    <td className="py-3 px-4 text-slate-500 text-xs">{r.reason || "-"}</td>
                    <td className="py-3 px-4 text-right text-rose-600 font-semibold">{rupiah(r.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )
      )}
    </>
  );
}
